import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, lazy, Suspense, useRef } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { motion, useInView } from 'motion/react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  PlacesAutocomplete,
  type PlaceResult,
} from '@/components/places-autocomplete';
import {
  type ServiceTierLevel,
  TRIP_MOTIVATIONS,
  type TripMotivation,
  type TripMotivationOption,
} from '../../shared/types/base';
import { type TripPreferences } from '../../shared/types/trip';
import { useTripPreferences } from '@/hooks/useTripPreferences';
import { useAuth } from '@/contexts/AuthContext';
import { useAuthFlowNavigation } from '@/hooks/useProfileCompletion';
import { ScrollText, ListOrdered, Map, GripVertical, Mail } from 'lucide-react';

const AuthModal = lazy(() => import('@/components/auth/AuthModal'));

const GOOGLE_PLACES_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY || '';

export const Route = createFileRoute('/')({
  component: WundraLandingPage,
});

/* ────────────────────────────────────────────
   Reusable animated section wrapper
──────────────────────────────────────────── */
function AnimatedSection({
  children,
  className = '',
  delay = 0,
  id,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  id?: string;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.section>
  );
}

/* ────────────────────────────────────────────
   Wundra Logo SVG (icon mark)
──────────────────────────────────────────── */
function WundraPin({ className = '' }: { className?: string }) {
  return (
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      className={className}
    >
      <path
        d="M12 2C8.13 2 5 5.13 5 9C5 14.25 12 22 12 22C12 22 19 14.25 19 9C19 5.13 15.87 2 12 2ZM12 11.5C10.62 11.5 9.5 10.38 9.5 9C9.5 7.62 10.62 6.5 12 6.5C13.38 6.5 14.5 7.62 14.5 9C14.5 10.38 13.38 11.5 12 11.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

/* ────────────────────────────────────────────
   Destination card data
──────────────────────────────────────────── */
const DESTINATIONS = [
  {
    name: 'Tokyo',
    tagline: 'The heart of neon culture',
    image:
      'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?q=80&w=800&auto=format&fit=crop',
    badge: 'Popular',
  },
  {
    name: 'New York',
    tagline: 'The city that never sleeps',
    image:
      'https://images.unsplash.com/photo-1496442226666-8d4d0e62e6e9?q=80&w=800&auto=format&fit=crop',
    badge: 'Popular',
  },
  {
    name: 'Paris',
    tagline: 'Romance in every alley',
    image:
      'https://images.unsplash.com/photo-1493150134366-cacb0bdc03fe?q=80&w=800&auto=format&fit=crop',
  },
  {
    name: 'Rome',
    tagline: 'History carved in stone',
    image:
      'https://images.unsplash.com/photo-1525874684015-58379d421a52?q=80&w=800&auto=format&fit=crop',
    badge: 'New',
  },
  {
    name: 'Cincinnati',
    tagline: 'River city charm and soul',
    image:
      'https://images.unsplash.com/photo-1600187230702-5f07aea6d7f2?q=80&w=800&auto=format&fit=crop',
  },
  {
    name: 'Reykjavik',
    tagline: 'Under the northern lights',
    image:
      'https://images.unsplash.com/photo-1504829857797-ddff29c27927?q=80&w=800&auto=format&fit=crop',
  },
  {
    name: 'Sedona',
    tagline: 'Red rocks and desert wonder',
    image:
      'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=800&auto=format&fit=crop',
    badge: 'Nature',
  },
  {
    name: 'Sydney',
    tagline: 'Harbor views and ocean breeze',
    image:
      'https://images.unsplash.com/photo-1506973035872-a4ec16b8e8d9?q=80&w=800&auto=format&fit=crop',
    badge: 'Popular',
  },
  {
    name: 'Santorini',
    tagline: 'Sunsets on the caldera',
    image:
      'https://images.unsplash.com/photo-1533105079780-92b9be482077?q=80&w=800&auto=format&fit=crop',
  },
];

/* ────────────────────────────────────────────
   Mock builder items
──────────────────────────────────────────── */
const BUILDER_ITEMS = [
  { name: 'Ichiran Shibuya', category: 'Food', color: 'bg-terra' },
  { name: 'Shibuya Sky', category: 'Nature', color: 'bg-sage' },
  { name: 'TeamLab Borderless', category: 'Culture', color: 'bg-clay' },
];

/* ────────────────────────────────────────────
   Three modality cards
──────────────────────────────────────────── */
const MODALITIES = [
  {
    label: 'Scrollytelling',
    icon: ScrollText,
    title: 'Interactive Story',
    description:
      'Relive your journey through a stunning vertically scrolling cinematic experience.',
  },
  {
    label: 'Timeline',
    icon: ListOrdered,
    title: 'Day-by-Day View',
    description:
      'A clean, structured list of stops, times, and essential logistics for each day.',
  },
  {
    label: 'Map View',
    icon: Map,
    title: 'Geospatial Map',
    description:
      'Visualize the exact path of your trip with optimized routing and travel times.',
  },
];

/* ════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════ */
function WundraLandingPage() {
  const navigate = useNavigate();
  const { setInitialPreferences } = useTripPreferences();
  const { currentUser } = useAuth();
  const { handleSuccessfulAuth } = useAuthFlowNavigation();

  const [selectedPlace, setSelectedPlace] = useState<PlaceResult | null>(null);
  const [selectedMotivation, setSelectedMotivation] =
    useState<TripMotivationOption>(TRIP_MOTIVATIONS[0]);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const handleMotivationChange = (value: string) => {
    const motivation = TRIP_MOTIVATIONS.find(m => m.id === value);
    if (motivation) {
      setSelectedMotivation(motivation);
    }
  };

  const handleStartPlanning = () => {
    if (!selectedPlace) return;

    const initialPrefs: TripPreferences = {
      serviceTier: 'self_service' as ServiceTierLevel,
      tripMotivation: selectedMotivation.id as TripMotivation,
      destination: {
        city: selectedPlace.city,
        country: selectedPlace.country,
        region: selectedPlace.region,
        coordinates: selectedPlace.coordinates,
        placeId: selectedPlace.placeId,
        address: null,
      },
      dates: {
        start: new Date(),
        end: new Date(),
      },
      travelers: {
        adults: 1,
        children: 0,
        infants: 0,
        groupType: selectedMotivation.defaultGroupType,
      },
      activityPreferences: {
        interests: [],
        priceLevel: 'moderate',
      },
    };

    setInitialPreferences(initialPrefs);

    if (currentUser) {
      navigate({ to: '/trip/plan' });
    } else {
      setShowAuthModal(true);
    }
  };

  return (
    <div className="min-h-screen bg-cream text-dusk w-full overflow-hidden">
      {/* ══════════════════════════════════════
         HERO — Split Screen
      ══════════════════════════════════════ */}
      <section className="flex flex-col lg:flex-row min-h-[90vh]">
        {/* Left: Headline + Search */}
        <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-20 lg:px-16 lg:py-0 bg-cream">
          <div className="max-w-xl">
            {/* Animated pin */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="mb-8 text-terra"
            >
              <WundraPin />
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-[0.9] text-dusk mb-8"
            >
              Your next <span className="text-terra">adventure</span> starts
              here
            </motion.h1>

            {/* Search form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.3,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="bg-warm-white p-6 rounded-[2rem] shadow-warm border border-sand space-y-4"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col space-y-1.5">
                  <label className="text-[11px] uppercase tracking-[0.15em] font-semibold text-warm-mid px-4">
                    Destination
                  </label>
                  <APIProvider apiKey={GOOGLE_PLACES_API_KEY}>
                    <PlacesAutocomplete
                      value={selectedPlace}
                      onPlaceSelect={setSelectedPlace}
                      placeholder="Where to?"
                      className="!h-14 !rounded-full !border-none !bg-cream hover:!bg-cream hover:!text-dusk focus-within:!ring-2 focus-within:!ring-terra"
                    />
                  </APIProvider>
                </div>

                <div className="flex flex-col space-y-1.5">
                  <label className="text-[11px] uppercase tracking-[0.15em] font-semibold text-warm-mid px-4">
                    Vibe
                  </label>
                  <Select
                    value={selectedMotivation.id}
                    onValueChange={handleMotivationChange}
                  >
                    <SelectTrigger className="w-full !h-14 !rounded-full !border-none !bg-cream focus:!ring-2 focus:!ring-terra">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TRIP_MOTIVATIONS.map(motivation => (
                        <SelectItem key={motivation.id} value={motivation.id}>
                          {motivation.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Button
                className="w-full h-14 rounded-full text-lg font-bold bg-terra hover:bg-rust text-warm-white transition-all hover:scale-[1.01] active:scale-[0.98] shadow-lg shadow-terra/20"
                onClick={handleStartPlanning}
                disabled={!selectedPlace}
              >
                {currentUser ? 'Build My Itinerary' : 'Sign In to Start'}
              </Button>
            </motion.div>
          </div>
        </div>

        {/* Right: Hero image + floating card */}
        <div className="w-full lg:w-1/2 relative bg-sand/20 flex items-center justify-center p-8 lg:p-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full max-h-[700px] overflow-hidden rounded-[3rem] shadow-2xl"
          >
            <img
              src="https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?q=80&w=2070&auto=format&fit=crop"
              alt="Destination"
              className="w-full h-full object-cover min-h-[400px]"
            />
            {/* Floating itinerary card */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.7,
                delay: 0.6,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="absolute top-10 right-10 w-72 bg-warm-white/90 backdrop-blur-md p-6 rounded-3xl shadow-xl border border-white/50"
            >
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-10 h-10 rounded-full bg-sand flex items-center justify-center">
                  <Map className="h-5 w-5 text-terra" />
                </div>
                <div>
                  <h4 className="font-bold text-sm text-dusk">Tokyo, Japan</h4>
                  <p className="text-xs text-warm-mid">
                    5 days · Solo Explorer
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="h-1.5 w-full bg-sand rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: '40%' }}
                    transition={{ duration: 1.2, delay: 1, ease: 'easeOut' }}
                    className="h-full bg-terra rounded-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2 text-[10px] uppercase font-bold tracking-widest">
                  <div className="bg-cream p-2 rounded-lg text-terra text-center">
                    32 Places
                  </div>
                  <div className="bg-cream p-2 rounded-lg text-terra text-center">
                    8 Food Stops
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
         DESTINATIONS — "Where to next?"
      ══════════════════════════════════════ */}
      <AnimatedSection className="relative py-24 px-6 lg:px-16 border-y border-sand">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
            <div className="max-w-xl">
              <h2 className="font-display text-4xl sm:text-6xl font-black text-dusk mb-4">
                Where to next?
              </h2>
              <p className="text-warm-mid text-lg font-light">
                Explore curated destinations optimized by local experts and our
                AI.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {DESTINATIONS.map((dest, i) => (
              <motion.div
                key={dest.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.1,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -8 }}
                className="group relative h-[450px] rounded-[2rem] overflow-hidden border-4 border-transparent hover:border-terra transition-all cursor-pointer shadow-lg"
              >
                <img
                  src={dest.image}
                  alt={dest.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dusk/80 via-transparent to-transparent" />
                {dest.badge && (
                  <div className="absolute top-6 right-6">
                    <span className="bg-terra text-warm-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg">
                      {dest.badge}
                    </span>
                  </div>
                )}
                <div className="absolute bottom-8 left-8">
                  <h3 className="font-display text-3xl font-bold text-warm-white">
                    {dest.name}
                  </h3>
                  <p className="text-warm-white/80 text-sm font-light">
                    {dest.tagline}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* ══════════════════════════════════════
         BUILDER — "Build your perfect day"
      ══════════════════════════════════════ */}
      <AnimatedSection id="builder" className="py-24 bg-dusk text-warm-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-16">
          <div className="text-center mb-20">
            <h2 className="font-display text-4xl sm:text-6xl font-black italic mb-6">
              Build your perfect day
            </h2>
            <p className="text-warm-white/50 text-lg max-w-2xl mx-auto font-light">
              Drag, drop, and rearrange. Our AI adjusts the logistics in
              real-time while you curate the vibes.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-8 bg-warm-white/5 p-4 rounded-[3rem] border border-white/10">
            {/* Left — Activity cards */}
            <div className="w-full lg:w-1/3 space-y-4 p-4">
              {BUILDER_ITEMS.map((item, i) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.15 }}
                  className={`p-4 bg-white/10 rounded-2xl border border-white/10 flex space-x-4 items-center ${
                    i === 1
                      ? 'rotate-[-2deg] scale-[1.05] shadow-2xl !border-terra relative z-20'
                      : ''
                  }`}
                >
                  <div className="w-12 h-12 rounded-lg bg-warm-white/10 flex items-center justify-center">
                    <GripVertical className="h-5 w-5 text-warm-white/40" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-sm">{item.name}</h4>
                    <span
                      className={`${item.color} text-[9px] px-2 py-0.5 rounded-full uppercase font-black text-warm-white inline-block mt-1`}
                    >
                      {item.category}
                    </span>
                  </div>
                  {i === 1 && (
                    <motion.div
                      animate={{ rotate: [0, -5, 5, 0] }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        repeatDelay: 2,
                      }}
                      className="absolute -bottom-3 -right-2 text-warm-white/60"
                    >
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M13.64 2.36a1 1 0 0 0-1.28 0l-9 8a1 1 0 0 0 1.28 1.54L5 11.54V21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-4h2v4a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-9.46l.36.36a1 1 0 1 0 1.28-1.54l-9-8Z" />
                      </svg>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Right — Day timeline */}
            <div className="w-full lg:w-2/3 space-y-6 p-8 bg-warm-white/5 rounded-[2rem]">
              <div className="flex items-center justify-between border-b border-white/10 pb-6">
                <h3 className="font-display text-2xl font-bold">
                  Day 1: Arrival & Exploration
                </h3>
                <div className="flex space-x-2">
                  <span className="w-3 h-3 rounded-full bg-terra" />
                  <span className="w-3 h-3 rounded-full bg-white/20" />
                  <span className="w-3 h-3 rounded-full bg-white/20" />
                </div>
              </div>

              <div className="space-y-4 relative">
                <div className="absolute left-[1.35rem] top-0 bottom-0 w-0.5 border-l-2 border-dashed border-white/10" />

                {/* Filled slot */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.3 }}
                  className="flex space-x-6 items-start relative"
                >
                  <div className="w-12 h-12 rounded-full bg-dusk border-2 border-terra flex items-center justify-center font-bold text-xs shrink-0">
                    09:00
                  </div>
                  <div className="flex-1 p-5 bg-white/5 rounded-2xl border border-white/5">
                    <h4 className="font-bold">Breakfast at Hotel</h4>
                    <p className="text-xs text-white/40">
                      Shinjuku Prince Hotel
                    </p>
                  </div>
                </motion.div>

                {/* Empty drop zone */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.5 }}
                  className="flex space-x-6 items-start relative"
                >
                  <div className="w-12 h-12 rounded-full bg-dusk border-2 border-white/10 flex items-center justify-center font-bold text-xs shrink-0 text-white/40">
                    11:30
                  </div>
                  <motion.div
                    animate={{
                      borderColor: [
                        'rgba(255,255,255,0.1)',
                        'rgba(196,98,45,0.4)',
                        'rgba(255,255,255,0.1)',
                      ],
                    }}
                    transition={{ duration: 2.5, repeat: Infinity }}
                    className="flex-1 p-5 border-2 border-dashed rounded-2xl flex items-center justify-center text-white/20 font-bold uppercase tracking-widest text-xs"
                  >
                    Drop activity here
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* ══════════════════════════════════════
         THREE MODALITIES
      ══════════════════════════════════════ */}
      <AnimatedSection className="py-24 px-6 lg:px-16 bg-cream">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {MODALITIES.map((mod, i) => (
              <motion.div
                key={mod.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{
                  duration: 0.6,
                  delay: i * 0.15,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{ y: -8 }}
                className="bg-warm-white p-10 rounded-[2.5rem] shadow-warm border border-sand transition-transform duration-500"
              >
                <div className="mb-6 inline-block">
                  <span className="bg-terra text-warm-white px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {mod.label}
                  </span>
                </div>
                <div className="h-64 bg-sand/20 rounded-2xl mb-8 relative overflow-hidden group flex items-center justify-center">
                  <mod.icon className="h-12 w-12 text-terra opacity-60 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500" />
                </div>
                <h3 className="font-display text-2xl font-bold mb-4">
                  {mod.title}
                </h3>
                <p className="text-warm-mid font-light leading-relaxed">
                  {mod.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-center"
          >
            <h2 className="font-display text-3xl sm:text-5xl font-black text-dusk">
              One trip. Three stunning views.
            </h2>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* ══════════════════════════════════════
         ABOUT
      ══════════════════════════════════════ */}
      <section
        id="about"
        className="py-24 px-6 lg:px-16 bg-sand/30 border-y border-sand"
      >
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-terra mb-4">
              About Wundra
            </p>
            <h2 className="font-display text-3xl sm:text-5xl font-black text-dusk mb-8">
              Wander with <em className="text-terra">wonder</em>
            </h2>
            <p className="text-warm-mid text-lg font-light leading-relaxed max-w-2xl mx-auto">
              Wundra is AI assisted travel planning that feels like dreaming. We
              combine intelligent recommendations with beautiful design so you
              can focus on the experience, not the logistics. Drop in
              restaurants, activities, and landmarks — Wundra turns them into a
              journey you'll actually want to take.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════
         BOTTOM CTA
      ══════════════════════════════════════ */}
      <AnimatedSection className="py-32 px-6 lg:px-16 bg-cream text-center border-b border-sand">
        <div className="max-w-8xl mx-auto">
          <h2 className="font-display text-4xl sm:text-8xl font-black tracking-tight text-dusk mb-12">
            Plan less. Explore more.
          </h2>
          <Button
            className="h-16 px-12 rounded-full text-xl font-bold bg-terra hover:bg-rust text-warm-white transition-all hover:scale-105 shadow-2xl shadow-terra/30"
            onClick={() => {
              if (currentUser) {
                navigate({ to: '/trip/plan' });
              } else {
                setShowAuthModal(true);
              }
            }}
          >
            Start Your First Trip
          </Button>
          <p className="mt-8 text-lg font-medium text-warm-mid">
            No Subscription Required
          </p>
          <div className="mt-4 flex items-center justify-center gap-3">
            <span className="text-4xl font-black font-display text-terra">
              As little as $8
            </span>
          </div>
        </div>
      </AnimatedSection>

      {/* ══════════════════════════════════════
         CONTACT
      ══════════════════════════════════════ */}
      <section id="contact" className="py-24 px-6 lg:px-16 bg-cream">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-terra mb-4">
              Get in Touch
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-black text-dusk mb-6">
              We'd love to hear from you
            </h2>
            <p className="text-warm-mid font-light mb-8">
              Questions, feedback, or partnership inquiries — drop us a line.
            </p>
            <a
              href="mailto:hello@wundra.app"
              className="inline-flex items-center gap-3 bg-terra hover:bg-rust text-warm-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-[1.02] shadow-lg shadow-terra/20"
            >
              <Mail className="h-5 w-5" />
              hello@wundra.app
            </a>
          </motion.div>
        </div>
      </section>

      {/* Auth Modal */}
      <Suspense fallback={null}>
        {showAuthModal && (
          <AuthModal
            isOpen={showAuthModal}
            onClose={() => setShowAuthModal(false)}
            onSuccess={() => {
              setShowAuthModal(false);
              handleSuccessfulAuth();
            }}
          />
        )}
      </Suspense>
    </div>
  );
}
